import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Card } from 'antd';
import { round } from '@turf/turf';
import { parseISODate } from '../utils';
import React, { useEffect, useRef } from 'react';


Highcharts.setOptions({
  time: {
    useUTC: false
  },
  lang: {
    months: [
      'Janvier', 'Février', 'Mars', 'Avril',
      'Mai', 'Juin', 'Juillet', 'Août',
      'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ],
    weekdays: [
      'Dimanche', 'Lundi', 'Mardi', 'Mercredi',
      'Jeudi', 'Vendredi', 'Samedi'
    ],
    shortMonths: [
      'Jan', 'Fév', 'Mar', 'Avr',
      'Mai', 'Juin', 'Juil', 'Août',
      'Sep', 'Oct', 'Nov', 'Déc'
    ],
    decimalPoint: ',',
    thousandsSep: ' '
  }
});


const FertilizationPHChart = ({ data }) => {
  const chartRef = useRef(null);

  //sort data by date
  data.sort((a, b) => parseISODate(a.reception_date) - parseISODate(b.reception_date));


  
    const options = {
      chart: {
        zoomType: 'xy',
        height: '500px',
        events: {
          load: function() {
            const chart = this;
            if (window.irrigationCharts) {
              window.irrigationCharts.push(chart);
            } else {
              window.irrigationCharts = [chart];
            }
          }
        }
      },
      title: { text: 'Fertilisation - pH' },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: { day: '%e %b' , hour: '%H:%M' },
        crosshair: true,
        events: {
          afterSetExtremes: function(e) {
            if (e.trigger === 'syncExtremes') return;
            
            const thisChart = this.chart;
            
            if (window.irrigationCharts) {
              window.irrigationCharts.forEach(chart => {
                if (chart !== thisChart && chart.xAxis && chart.xAxis[0]) {
                  if (e.min === undefined || e.max === undefined) {
                    chart.xAxis[0].setExtremes(null, null, true, false, { trigger: 'syncExtremes' });
                  } else {
                    chart.xAxis[0].setExtremes(e.min, e.max, true, false, { trigger: 'syncExtremes' });
                  }
                }
              });
            }
          }
        }
      },
      yAxis: [
        {
          labels: { format: '{value}', style: { color: Highcharts.getOptions().colors[0] } },
          title: { text: 'pH', style: { color: Highcharts.getOptions().colors[0] } }
        },
        {
          title: { text: 'Volume Acide (L)', style: { color: Highcharts.getOptions().colors[2] } },
          labels: { format: '{value} L', style: { color: Highcharts.getOptions().colors[2] } },
          opposite: true
        }
      ],
      tooltip: {
      shared: true,
      crosshairs: true,
      formatter: function() {
        const date = Highcharts.dateFormat('%e %b %Y %H:%M', this.x);
        let tooltip = `<b>${date}</b><br/>`;
    
        if (this.points && Array.isArray(this.points)) {
          this.points.forEach(point => {
            tooltip += `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${point.y}</b><br/>`;
          });
        }
    
        return tooltip;
      }
    },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
      },
      plotOptions: {
        series: {
          connectNulls: true,
          marker: {
            enabled: false
          },
          point: {
            events: {
              mouseOver: function() {
                const chart = this.series.chart;
                const x = this.x;
                
                if (window.irrigationCharts) {
                  window.irrigationCharts.forEach(otherChart => {
                    if (otherChart !== chart && otherChart.series && Array.isArray(otherChart.series)) {
                      const points = otherChart.series.map(s => {
                        return s.searchPoint({ chartX: otherChart.xAxis[0].toPixels(x) }, true);
                      }).filter(p => p);
                      
                      if (points.length > 0 && otherChart.tooltip && otherChart.xAxis[0]) {
                        otherChart.tooltip.refresh(points);
                        otherChart.xAxis[0].drawCrosshair(null, points[0]);
                      }
                    }
                  });
                }
              },
              mouseOut: function() {
                if (window.irrigationCharts) {
                  window.irrigationCharts.forEach(chart => {
                    if (chart.tooltip) {
                      chart.tooltip.hide();
                    }
                    if (chart.xAxis && chart.xAxis[0]) {
                      chart.xAxis[0].hideCrosshair();
                    }
                  });
                }
              }
            }
          },
          events: {
            mouseOut: function() {
              if (window.irrigationCharts) {
                window.irrigationCharts.forEach(chart => {
                  if (chart.tooltip) {
                    chart.tooltip.hide();
                  }
                  if (chart.xAxis && chart.xAxis[0]) {
                    chart.xAxis[0].hideCrosshair();
                  }
                });
              }
            }
          }
        },
        spline: {
          connectNulls: true,
          marker: {
            enabled: false
          }
        }
      },
      series: [
        // Columns first (rendered behind)
        { name: 'Volume Acide (L)', type: 'column', yAxis: 1, data: data.map(d => [parseISODate(d.reception_date), round(d.volume_acide, 2)]).filter(d => d[1] !== null), pointWidth: 10 , visible: false},
        // Splines/Scatter last (rendered in front)
        { name: 'pH', type: 'spline', yAxis: 0, data: data.map(d => [parseISODate(d.reception_date), d.ph]).filter(d => d[1] !== null), connectNulls: true, marker: { enabled: false } },
        { 
          name: 'pH Irrigation', 
          type: 'scatter', 
          yAxis: 0, 
          data: data.map(d => [parseISODate(d.reception_date), d.ph_moyenne]).filter(d => d[1] !== null),
          marker: {
            radius: 8,
            symbol: 'circle',
            enabled: true
          }
        },
      ],
      responsive: {
        rules: [{
          condition: { maxWidth: 500 },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom'
            }
          }
        }]
      },
    };
  
    useEffect(() => {
      return () => {
        if (window.irrigationCharts && chartRef.current) {
          const chart = chartRef.current.chart;
          window.irrigationCharts = window.irrigationCharts.filter(c => c !== chart);
        }
      };
    }, []);

    return (
      <Card style={{ marginBottom: 16 }}>
        <HighchartsReact 
          highcharts={Highcharts} 
          options={options} 
          ref={chartRef}
        />
      </Card>
    );
  };

  export default FertilizationPHChart;